import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum EightSleepPORmbLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  name = 'name', // descriptive name of the item
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const EightSleepPORmbAddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
})
export type EightSleepPORmbFileAddressData = yup.InferType<typeof EightSleepPORmbAddressInfoSchema>

export const EightSleepPORmbFileDataSchema = yup.object().shape({
  issuer: EightSleepPORmbAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: EightSleepPORmbAddressInfoSchema,
  vendor: EightSleepPORmbAddressInfoSchema,
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  items2: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  notes: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
})

export type EightSleepPORmbFileData = yup.InferType<typeof EightSleepPORmbFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<EightSleepPORmbFileData>

/**
 * These UUIDs become `GeneratedFileField.id` primary keys, so they must be unique across every
 * template — they are deliberately fresh rather than copied from `EightSleepPOFileMappings`.
 * Reusing one would make `resetGeneratedFileFields` (which looks rows up filtered by file type)
 * miss the existing row, take the create path, and fail on a primary-key conflict.
 */
export const EightSleepPORmbFileMappings: RequiredFileData = {
  issuer: {
    attn: '578de39d-75f8-4449-ad28-4d9ebc721ab4',
    companyName: 'f10a41ab-d241-4975-a976-7195b14ef39d',
    addressLine1: '63b3756a-842d-4c23-870a-deed0a8bf472',
    addressLine2: '06736c0c-44da-40ce-9c66-5c5a285835fd',
    city: 'c8f4e1ff-b62b-4880-9a21-99748276c0fe',
    state: '8bf66120-53ff-47a0-8f89-7cb90a8c660f',
    zipCode: '3fb64f61-6ed2-4bf6-a392-295b5857f7e7',
    country: '488e666b-c53a-4fa4-8a5f-2a4b308218f2',
    phoneNumber: '2bfad25a-a4a7-4804-987d-3e5994b76a55',
    emailAddress: '86503444-b984-451d-a012-51d6f90a939d',
    displayMultipleLines: 'fad6ca5b-4c8c-4c55-91c4-74f44caa6530',
  },
  orderNumber: '145aee92-9a2c-460d-973e-35a0d8025ed2',
  paymentTerms: 'ec710648-be55-4013-8c51-b610bbcd3cbc',
  freightTerms: '80d4887a-5fe0-4374-84f3-ddc2ce6ba239',
  dateIssued: 'f70127fc-0028-4a02-b550-31e1a999b2ae',
  dateReady: '7398c5cb-2723-4d89-b0a9-279c0c61885d',
  destination: {
    attn: '03e47495-2b2e-4274-b27f-6e1ab0917abb',
    companyName: 'c36da6a7-d538-4161-89d7-ebbbeaeb05e5',
    addressLine1: 'de3fa87f-501b-4494-a1c7-c354cc573473',
    addressLine2: '18d2ba4f-b593-479d-ac8e-71404066b279',
    city: '087a89d8-0817-4324-b8dc-58bd64c36ec9',
    state: '85396e1b-fe8d-4a68-bd57-9b760c4b14bc',
    zipCode: '46439133-137e-4daf-a51e-9bb3006c21e8',
    country: '7fa0752e-48f5-48ad-aa40-7cd0ffdd7922',
    phoneNumber: '18f72f89-d288-42c8-b3b4-903882f23747',
    emailAddress: '957d49e7-917f-4e13-b0e7-731f0516833a',
    displayMultipleLines: '5146a00c-bec5-4011-8d95-c230b5bb321b',
  },
  vendor: {
    attn: 'f96ed8e7-915f-477f-bd71-6c91c19b1c80',
    companyName: '49f23258-9ff3-41ad-ab08-a206b0638cff',
    addressLine1: 'c90a0aa3-ac16-4158-bd5a-270aca2efc7d',
    addressLine2: '93fe79ac-94d5-40e0-adf3-811daaa36ffd',
    city: '0cb66002-ffc3-4b44-a838-1024ca7249b0',
    state: '591822f5-e214-4943-a9e6-9de6b8e06a85',
    zipCode: 'c4cb2fce-69f3-4536-bf65-a0eacdca0c36',
    country: 'ff7878cf-b640-449c-abbf-4756530b6e6d',
    phoneNumber: 'b6a7e271-ac0c-483b-a419-2b2ea68d9b75',
    emailAddress: '8b6fd8d8-3230-441c-ae3c-b1da4469839e',
    displayMultipleLines: 'ec973a57-d48a-42ae-95a8-4c6c034f51ae',
  },
  items: [
    {
      itemNumber: null,
      itemName: 'e096c926-79b2-4561-aa37-c7bb2c558301',
      name: '130e5600-6251-4a93-96c2-66660f58da9e',
      description: 'ae155b13-7aeb-4dcc-88c7-62c1f2391d13',
      itemQty: '4ce513aa-ebca-4bb3-9af7-2d83ec843f1c',
      unitPrice: 'a6df8b57-b8f1-4599-af7d-d6a6d660611f',
      lineTotal: null,
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: 'bdab33b7-040d-4077-81e5-7cc72098bdd6',
      name: '60d6aad0-0635-4d41-94cc-fcff67f34e24',
      description: '52c61173-46b1-42c5-9342-482c3739c640',
      itemQty: 'b310caa9-3ed4-49e5-8d09-a89cf1678c61',
      unitPrice: '2e3c43d1-71d5-4495-ba22-32074f855793',
      lineTotal: null,
    },
  ],
  dontRoundUnitPrice: '43b9eee6-f55e-4d41-8a6b-f66178b28580',
  agreement: '7b0ce983-17d3-4719-bba5-69b5ba0ad175',
  notes: 'c45a2086-e0b1-48b5-8e8b-6fdf26f935d5',
  instructions: '9f464d9e-109f-4b94-8c76-43c04ff588c0',
  useLocaleQuantityFormatting: '927081ae-4a65-4751-9fb6-defcd25b0357',
}
